import { useQuery } from '@apollo/client/react';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import { StatusBar } from 'expo-status-bar';
import { memo, useCallback, useMemo, useState } from 'react';
import { FlatList, Modal, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { ActivityIndicator, Appbar, Chip, Icon, Surface, Text } from 'react-native-paper';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';

import { fontSize, lineHeight, spacing, useAppTheme } from '@/theme';

import {
  AppbarBackButton,
  AppSearchbar,
  OutlineButton,
  PrimaryButton,
  ScreenAppbar,
} from '@/components/atoms';
import { ContactNotesSheet, ContactSelectionCard } from '@/components/molecules/list';
import { ContactProfile } from '@/components/organisms/contacts/ContactProfile';
import { formatLastAction } from '@/formatters/lastAction';
import {
  GetContactStatusesDocument,
  GetContactsDocument,
  type GetContactsQuery,
  type GetContactsQueryVariables,
} from '@/generated/usermobile-bff/graphql';
import { useDebounce, usePaginatedFetchMore } from '@/hooks';
import { useTranslation } from '@/i18n';
import type { ContactPickerCloseAction } from '@/models/contactPickerAnalytics';

export interface PickerSelectedContact {
  readonly id: number;
  readonly name: string;
}

interface ContactPickerDialogProps {
  readonly onDismiss: (
    action: ContactPickerCloseAction,
    contacts: readonly PickerSelectedContact[],
  ) => void;
  readonly onConfirm: (contacts: readonly PickerSelectedContact[]) => void;
  readonly isSubmitting?: boolean;
  readonly initialSelectedIds?: readonly number[];
  readonly initialSelectedContacts?: readonly PickerSelectedContact[];
  readonly onSkip?: (contacts: readonly PickerSelectedContact[]) => void;
  readonly confirmLabel?: string;
  readonly skipLabel?: string;
  readonly title?: string;
  readonly maxSelection?: number;
}

interface ContactPickerRowProps {
  readonly item: GetContactsQuery['contacts']['data'][number];
  readonly contactStatusNames: ReadonlyMap<number, string>;
  readonly noStatusLabel: string;
  readonly selected: boolean;
  readonly disabled: boolean;
  readonly onToggle: (contact: GetContactsQuery['contacts']['data'][number]) => void;
  readonly onNotesPress: (contact: GetContactsQuery['contacts']['data'][number]) => void;
  readonly onViewProfile: (contact: GetContactsQuery['contacts']['data'][number]) => void;
  readonly seeMemoLabel: string;
  readonly viewProfileLabel: string;
}

interface ContactNotesSheetState {
  readonly visible: boolean;
  readonly contactName: string;
  readonly notes: string;
}

const PER_PAGE = 20;
const ORDER_BY = 'last_action_occurred_at desc';

const keyExtractor = (item: GetContactsQuery['contacts']['data'][number]) => String(item.id);

const createContactListInput = (
  page: number,
  search: string,
): NonNullable<GetContactsQueryVariables['input']> => ({
  page,
  perPage: PER_PAGE,
  search: search || undefined,
  orderBy: ORDER_BY,
});

const getContactSubtitle = (
  contact: GetContactsQuery['contacts']['data'][number],
  contactStatusNames: ReadonlyMap<number, string>,
  noStatusLabel: string,
  t: (key: string, options?: Record<string, unknown>) => string,
) => {
  const statusName =
    contact.contactStatusId != null
      ? (contactStatusNames.get(contact.contactStatusId) ?? noStatusLabel)
      : noStatusLabel;

  return t('contacts.picker.statusLabel', {
    name: statusName,
    defaultValue: `Status: ${statusName}`,
  });
};

const syncSelectedContacts = (
  selectedContacts: readonly PickerSelectedContact[],
  contacts: readonly GetContactsQuery['contacts']['data'][number][],
): readonly PickerSelectedContact[] => {
  const contactNames = new Map(contacts.map((contact) => [contact.id, contact.displayName]));

  return selectedContacts.map((contact) => ({
    id: contact.id,
    name: contactNames.get(contact.id) ?? contact.name,
  }));
};

const ContactPickerRow = memo(
  ({
    item,
    contactStatusNames,
    noStatusLabel,
    selected,
    disabled,
    onToggle,
    onNotesPress,
    onViewProfile,
    seeMemoLabel,
    viewProfileLabel,
  }: ContactPickerRowProps) => {
    const { t } = useTranslation();

    const handleToggle = useCallback(() => {
      onToggle(item);
    }, [onToggle, item]);

    const handleNotesPress = useCallback(() => {
      onNotesPress(item);
    }, [item, onNotesPress]);

    const handleViewProfile = useCallback(() => {
      onViewProfile(item);
    }, [item, onViewProfile]);

    const subtitle = getContactSubtitle(item, contactStatusNames, noStatusLabel, t);
    const lastActionText = formatLastAction({
      lastActionType: item.lastActionType,
      lastActionOccurredAt: item.lastActionOccurredAt,
      t,
    });

    return (
      <ContactSelectionCard
        title={item.displayName}
        subtitle={subtitle}
        subtitleColor={null}
        notes={item.notes}
        lastActionText={lastActionText}
        selected={selected}
        disabled={disabled}
        onToggle={handleToggle}
        onNotesPress={handleNotesPress}
        onViewProfile={handleViewProfile}
        seeMemoLabel={seeMemoLabel}
        viewProfileLabel={viewProfileLabel}
      />
    );
  },
);
ContactPickerRow.displayName = 'ContactPickerRow';

const styles = StyleSheet.create({
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    flex: 1,
    justifyContent: 'center',
    padding: 16,
  },
  surfaceWrapper: {
    borderRadius: 28,
    flex: 1,
    maxHeight: '90%',
    overflow: 'hidden',
  },
  surface: {
    borderRadius: 28,
    flex: 1,
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  title: {
    color: '#17344a',
    fontFamily: 'Lato-Bold',
    fontSize: 20,
    fontWeight: '700',
    lineHeight: 32,
    marginBottom: 16,
  },
  searchBar: {
    marginBottom: 16,
  },
  chipsArea: {
    flexGrow: 0,
    height: 36,
    marginBottom: spacing[2],
  },
  chipsContent: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },
  errorText: {
    margin: 20,
    textAlign: 'center',
  },
  emptyContainer: {
    alignItems: 'center',
    flex: 1,
    gap: spacing[2],
    justifyContent: 'center',
    paddingHorizontal: spacing[4],
  },
  emptyTitle: {
    fontSize: fontSize.xxl,
    textAlign: 'center',
  },
  emptyDescription: {
    fontSize: fontSize.sm,
    lineHeight: lineHeight.sm,
    textAlign: 'center',
  },
  footerLoader: {
    marginVertical: 10,
  },
  footer: {
    flexDirection: 'row',
    gap: 10,
    paddingBottom: 16,
    paddingTop: 32,
  },
  footerButton: {
    flex: 1,
  },
  loadingContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  list: {
    flex: 1,
  },
  listContent: {
    flexGrow: 1,
    gap: 8,
    paddingTop: spacing[2],
  },
  profileContainer: {
    flex: 1,
  },
});

export const ContactPickerDialog = ({
  onDismiss,
  onConfirm,
  isSubmitting,
  initialSelectedIds,
  initialSelectedContacts,
  onSkip,
  confirmLabel,
  skipLabel,
  title,
  maxSelection,
}: ContactPickerDialogProps) => {
  const { t } = useTranslation();
  const theme = useAppTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [viewingContact, setViewingContact] = useState<PickerSelectedContact | null>(null);
  const [contactNotesSheet, setContactNotesSheet] = useState<ContactNotesSheetState>({
    visible: false,
    contactName: '',
    notes: '',
  });
  const resolvedTitle = title ?? t('recording.contactPicker.title');
  const [selectedContacts, setSelectedContacts] = useState<readonly PickerSelectedContact[]>(() => {
    if (initialSelectedContacts?.length) {
      return initialSelectedContacts;
    }

    return (initialSelectedIds ?? []).map((id) => ({ id, name: String(id) }));
  });
  const queryInput = useMemo(
    () => createContactListInput(1, debouncedSearchQuery),
    [debouncedSearchQuery],
  );

  const { data, loading, error, fetchMore, networkStatus } = useQuery(GetContactsDocument, {
    variables: { input: queryInput },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const syncedSelectedContacts = useMemo(
    () => syncSelectedContacts(selectedContacts, data?.contacts.data ?? []),
    [selectedContacts, data?.contacts.data],
  );
  const selectedIds = useMemo(
    () => new Set(syncedSelectedContacts.map((contact) => contact.id)),
    [syncedSelectedContacts],
  );

  const { data: statusesData } = useQuery(GetContactStatusesDocument, {
    fetchPolicy: 'cache-first',
  });

  const buildVariables = useMemo(
    () => (nextPage: number) => ({
      input: createContactListInput(nextPage, debouncedSearchQuery),
    }),
    [debouncedSearchQuery],
  );

  const { isFetchingMore, handleEndReached } = usePaginatedFetchMore<GetContactsQuery>({
    networkStatus,
    data,
    entityKey: 'contacts',
    fetchMore,
    buildVariables,
  });
  const contactStatusNames = useMemo(
    () =>
      new Map(
        (statusesData?.contactStatuses?.data ?? []).flatMap((status) =>
          status.id == null || status.name == null ? [] : [[status.id, status.name] as const],
        ),
      ),
    [statusesData],
  );
  const noStatusLabel = t('contacts.profile.noStatus', { defaultValue: 'No Status' });
  const seeMemoLabel = t('contacts.picker.seeMemo');
  const viewProfileLabel = t('contacts.picker.viewProfile');
  const contacts = data?.contacts.data ?? [];

  const isMaxReached = maxSelection != null && syncedSelectedContacts.length >= maxSelection;

  const handleToggle = useCallback(
    (contact: GetContactsQuery['contacts']['data'][number]) => {
      if (selectedIds.has(contact.id)) {
        setSelectedContacts((current) =>
          current.filter((selectedContact) => selectedContact.id !== contact.id),
        );

        return;
      }

      if (isMaxReached) {
        return;
      }

      setSelectedContacts((current) => [...current, { id: contact.id, name: contact.displayName }]);
    },
    [isMaxReached, selectedIds],
  );

  const handleRemoveSelectedContact = useCallback((contactId: number) => {
    setSelectedContacts((current) =>
      current.filter((selectedContact) => selectedContact.id !== contactId),
    );
  }, []);

  const handleNotesPress = useCallback((contact: GetContactsQuery['contacts']['data'][number]) => {
    setContactNotesSheet({
      visible: true,
      contactName: contact.displayName,
      notes: contact.notes ?? '',
    });
  }, []);

  const handleViewProfile = useCallback((contact: GetContactsQuery['contacts']['data'][number]) => {
    setViewingContact({ id: contact.id, name: contact.displayName });
  }, []);

  const handleCloseProfile = useCallback(() => {
    setViewingContact(null);
  }, []);

  const handleDismissNotesSheet = useCallback(() => {
    setContactNotesSheet((current) => ({ ...current, visible: false }));
  }, []);

  const handleConfirm = useCallback(() => {
    onConfirm(syncedSelectedContacts);
  }, [onConfirm, syncedSelectedContacts]);

  const handleCancel = useCallback(() => {
    onDismiss('cancel', syncedSelectedContacts);
  }, [onDismiss, syncedSelectedContacts]);

  // Hardware back closes the dialog without an explicit button choice, same as a backdrop tap.
  const handleBackdropDismiss = useCallback(() => {
    onDismiss('backdrop', syncedSelectedContacts);
  }, [onDismiss, syncedSelectedContacts]);

  const handleSkip = useCallback(() => {
    onSkip?.(syncedSelectedContacts);
  }, [onSkip, syncedSelectedContacts]);

  const contactRowItem = useCallback(
    ({ item }: { item: GetContactsQuery['contacts']['data'][number] }) => {
      const selected = selectedIds.has(item.id);

      return (
        <ContactPickerRow
          item={item}
          contactStatusNames={contactStatusNames}
          noStatusLabel={noStatusLabel}
          selected={selected}
          disabled={isMaxReached && !selected}
          onToggle={handleToggle}
          onNotesPress={handleNotesPress}
          onViewProfile={handleViewProfile}
          seeMemoLabel={seeMemoLabel}
          viewProfileLabel={viewProfileLabel}
        />
      );
    },
    [
      contactStatusNames,
      handleNotesPress,
      handleToggle,
      handleViewProfile,
      isMaxReached,
      noStatusLabel,
      seeMemoLabel,
      selectedIds,
      viewProfileLabel,
    ],
  );

  return (
    <>
      <Modal visible transparent animationType="fade" onRequestClose={handleBackdropDismiss}>
        <GestureHandlerRootView style={{ flex: 1 }}>
          <BottomSheetModalProvider>
            <View style={styles.overlay}>
              <Pressable
                style={StyleSheet.absoluteFill}
                onPress={handleBackdropDismiss}
                testID="contact-picker-backdrop"
              />
              <View style={styles.surfaceWrapper}>
                <Surface
                  style={[styles.surface, { backgroundColor: theme.colors.background }]}
                  elevation={5}>
                  <View style={styles.content}>
                    <Text style={styles.title}>{resolvedTitle}</Text>
                    <AppSearchbar
                      placeholder={t('contacts.picker.searchPlaceholder')}
                      onChangeText={setSearchQuery}
                      value={searchQuery}
                      elevation={0}
                      iconColor={
                        searchQuery ? theme.colors.textNdPrimary : theme.colors.textNdMuted
                      }
                      placeholderTextColor={theme.colors.textNdMuted}
                      inputStyle={{
                        color: theme.colors.textNdPrimary,
                        includeFontPadding: false,
                        lineHeight: 20,
                        minHeight: 0,
                      }}
                      style={[
                        styles.searchBar,
                        {
                          backgroundColor: searchQuery
                            ? theme.colors.background
                            : theme.colors.bgNlBase,
                          borderWidth: 1,
                          borderColor: searchQuery
                            ? theme.colors.textNdPrimary
                            : theme.colors.outline,
                        },
                      ]}
                    />
                    <ScrollView
                      style={styles.chipsArea}
                      contentContainerStyle={styles.chipsContent}>
                      {syncedSelectedContacts.map((contact) => (
                        <Animated.View
                          key={contact.id}
                          entering={FadeIn.duration(150)}
                          exiting={FadeOut.duration(150)}>
                          <Chip
                            mode="outlined"
                            textStyle={{ color: theme.colors.textNdPrimary }}
                            closeIcon={({ size }) => (
                              <Icon source="close" size={size} color={theme.colors.textNdPrimary} />
                            )}
                            style={{ borderRadius: 4, borderColor: theme.colors.outline }}
                            onClose={() => handleRemoveSelectedContact(contact.id)}>
                            {contact.name}
                          </Chip>
                        </Animated.View>
                      ))}
                    </ScrollView>
                    {loading && !data ? (
                      <ActivityIndicator style={styles.loadingContainer} />
                    ) : null}
                    {error && !data ? (
                      <Text style={[styles.errorText, { color: theme.colors.error }]}>
                        {t('contacts.loadError')}
                      </Text>
                    ) : null}
                    {data ? (
                      <FlatList
                        style={styles.list}
                        data={contacts}
                        keyExtractor={keyExtractor}
                        renderItem={contactRowItem}
                        onEndReached={handleEndReached}
                        onEndReachedThreshold={0.5}
                        contentContainerStyle={styles.listContent}
                        ListEmptyComponent={
                          <View style={styles.emptyContainer}>
                            <Text
                              style={[styles.emptyTitle, { color: theme.colors.textNdPrimary }]}>
                              {t('contacts.empty')}
                            </Text>
                            <Text
                              style={[
                                styles.emptyDescription,
                                { color: theme.colors.textNdSecondary },
                              ]}>
                              {t('contacts.emptyDescription')}
                            </Text>
                          </View>
                        }
                        ListFooterComponent={
                          isFetchingMore ? (
                            <ActivityIndicator size="small" style={styles.footerLoader} />
                          ) : null
                        }
                      />
                    ) : null}
                    <View style={styles.footer} testID="contact-picker-footer">
                      {onSkip ? (
                        <OutlineButton
                          onPress={handleSkip}
                          disabled={Boolean(isSubmitting)}
                          style={styles.footerButton}
                          testID="contact-picker-dismiss">
                          {skipLabel ?? t('common.skip', { defaultValue: 'Skip' })}
                        </OutlineButton>
                      ) : (
                        <OutlineButton
                          onPress={handleCancel}
                          disabled={Boolean(isSubmitting)}
                          style={styles.footerButton}
                          testID="contact-picker-dismiss">
                          {t('common.cancel')}
                        </OutlineButton>
                      )}
                      <PrimaryButton
                        onPress={handleConfirm}
                        loading={Boolean(isSubmitting)}
                        disabled={Boolean(isSubmitting) || syncedSelectedContacts.length === 0}
                        style={styles.footerButton}
                        testID="contact-picker-confirm">
                        {confirmLabel ?? t('recording.contactPicker.confirm')}
                      </PrimaryButton>
                    </View>
                  </View>
                </Surface>
              </View>
            </View>
            <Modal
              visible={viewingContact != null}
              transparent={false}
              animationType="slide"
              onRequestClose={handleCloseProfile}>
              <GestureHandlerRootView style={styles.profileContainer}>
                <BottomSheetModalProvider>
                  <StatusBar style="light" />
                  <ScreenAppbar variant="dark">
                    <AppbarBackButton onPress={handleCloseProfile} color="#ffffff" />
                    <Appbar.Content
                      title={t('contacts.picker.profileScreenTitle', {
                        name: viewingContact?.name ?? '',
                        defaultValue: `${viewingContact?.name ?? ''} Details`,
                      })}
                    />
                  </ScreenAppbar>
                  {viewingContact && <ContactProfile id={viewingContact.id} />}
                </BottomSheetModalProvider>
              </GestureHandlerRootView>
            </Modal>
            <ContactNotesSheet
              visible={contactNotesSheet.visible}
              contactName={contactNotesSheet.contactName}
              notes={contactNotesSheet.notes}
              onDismiss={handleDismissNotesSheet}
            />
          </BottomSheetModalProvider>
        </GestureHandlerRootView>
      </Modal>
    </>
  );
};
